import { useEffect, useState } from "react";
import { addWeeks } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { weekStartOf } from "@/lib/time";
import { usePlanner } from "@/hooks/usePlanner";
import WeekToolbar from "@/components/planner/WeekToolbar";
import WeekCalendar from "@/components/planner/WeekCalendar";
import TaskBacklog from "@/components/planner/TaskBacklog";
import TaskDialog from "@/components/planner/TaskDialog";
import EventDialog from "@/components/planner/EventDialog";
import FocusCard from "@/components/planner/FocusCard";
import AiPanel from "@/components/planner/AiPanel";

export default function Planner() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(weekStartOf(new Date()));
  const [viewUserId, setViewUserId] = useState("");
  const [team, setTeam] = useState([]);
  const [taskDialog, setTaskDialog] = useState(null);
  const [eventDialog, setEventDialog] = useState(null);
  const [planning, setPlanning] = useState(false);
  const { tasks, events, overrides, insight, setInsight, replan, ws } = usePlanner(weekStart, viewUserId);
  const viewed = team.find((m) => m.id === viewUserId) || user;

  useEffect(() => {
    if (user.role === "admin") {
      api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    }
  }, [user.role]);

  const autoPlan = async () => {
    setPlanning(true);
    try {
      await replan(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPlanning(false);
    }
  };

  const toggleDone = async (task) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: task.status === "done" ? "todo" : "done" });
      await replan();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const moveBlock = async (id, from, start) => {
    try {
      await api.post(`/tasks/${id}/move`, { from_start: from, start });
      await replan();
      toast.success("Task pinned to its new time. The rest of your week re-planned around it.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="planner-page">
      <WeekToolbar
        weekStart={weekStart}
        onPrev={() => setWeekStart(addWeeks(weekStart, -1))}
        onNext={() => setWeekStart(addWeeks(weekStart, 1))}
        onToday={() => setWeekStart(weekStartOf(new Date()))}
        onAutoPlan={autoPlan}
        planning={planning}
        onAddTask={() => setTaskDialog({})}
        onAddEvent={() => setEventDialog({})}
        team={team}
        viewUserId={viewUserId}
        onViewUser={setViewUserId}
        viewedName={viewed?.name}
      />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 space-y-6 min-w-0">
          <FocusCard tasks={tasks} events={events} onOpen={(t) => setTaskDialog(t)} />
          <WeekCalendar
            weekStart={weekStart}
            settings={viewed?.settings || user.settings}
            tasks={tasks}
            events={events}
            overrides={overrides}
            onTaskClick={(t) => setTaskDialog(t)}
            onEventClick={(e) => setEventDialog(e)}
            onMoveBlock={moveBlock}
          />
        </div>
        <aside className="xl:col-span-4 space-y-6">
          <AiPanel insight={insight} setInsight={setInsight} ws={ws} viewUserId={viewUserId} />
          <TaskBacklog tasks={tasks} onOpen={(t) => setTaskDialog(t)} onToggle={toggleDone} onAdd={() => setTaskDialog({})} />
        </aside>
      </div>
      <TaskDialog
        task={taskDialog}
        onClose={() => setTaskDialog(null)}
        onSaved={() => replan()}
        team={team}
        defaultUserId={viewUserId}
      />
      <EventDialog
        event={eventDialog}
        weekStart={weekStart}
        onClose={() => setEventDialog(null)}
        onSaved={() => replan()}
        viewUserId={viewUserId}
      />
    </div>
  );
}
